import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Pagination, Table, TableAction, TableActions, TableData, TableDataTypes } from '../../shared/types';
import { LoaderService } from '../../loader.service';
import { AlertDialogService } from '../../shared/alert-dialog/alert-dialog.service';
import { SubscriptionService } from '../subscription.service';
import { AuthzAccessHintService } from '../../shared/authz-access-hint/authz-access-hint.service';
import { AUTHZ_FEATURES } from '../../constants/permissions';

@Component({
  selector: 'app-config-request-list',
  templateUrl: './list.component.html'
})
export class ConfigRequestListComponent implements OnInit {
  public title = 'Config Requests';
  public authzAccessHint: { message: string; requestUrl: string } | null = null;
  public table: Table;
  private kind = 'subscription';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private loaderService: LoaderService,
    private alertDialogService: AlertDialogService,
    private authzAccessHintService: AuthzAccessHintService,
  ) {
    const data = this.route.snapshot.data || {};
    this.kind = data.kind || 'subscription';
    this.title = data.title || this.title;
    this.table = new Table({
      headers: this.headers(),
      pagination: new Pagination(0, 15, 0, [15, 20]),
      data: [],
      sortableColumnKeys: []
    });
  }

  async ngOnInit() {
    const feature = this.kind === 'billingPlan' ? AUTHZ_FEATURES.BILLING_PLANS : AUTHZ_FEATURES.SUBSCRIPTION;
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData(feature);
    this.fetchTableData();
  }

  fetchTableData() {
    this.loaderService.openLoading();
    const page = this.table.pagination.pageNo || 0;
    const limit = this.table.pagination.perPage || 15;
    this.subscriptionService.getConfigRequests(this.kind, page, limit).subscribe(
      (response: any) => {
        const rows = (response && response.data) || [];
        this.table.pagination.count = (response && response.documentCount) || 0;
        this.table.data = rows.map(row => this.toRow(row));
        this.loaderService.closeLoading();
      },
      (error) => {
        this.table.data = [];
        this.loaderService.closeLoading();
        const body = error && error.error;
        const message = (body && (body.message || body.errorMessage)) || 'Could not load config requests';
        this.alertDialogService.open({ title: 'Config requests', message, confirmText: 'OK' });
      }
    );
  }

  private headers() {
    if (this.kind === 'billingPlan') {
      return {
        status: 'Status',
        cities: 'City',
        zoneNames: 'Zone',
        name: 'Name',
        serviceNames: 'Service',
        startDate: 'Start Date',
        endDate: 'End Date',
        userSelector: 'User Selector',
        priority: 'Priority',
        action: 'Actions'
      };
    }
    return {
      status: 'Status',
      cities: 'City',
      name: 'Name',
      serviceNames: 'Service',
      startDate: 'Start Date',
      endDate: 'End Date',
      type: 'Type',
      priority: 'Priority',
      action: 'Actions'
    };
  }

  private toRow(row) {
    const common = {
      id: row._id,
      status: this.statusLabel(row.requestStatus),
      cities: row.cities || '--',
      name: row.name || '--',
      serviceNames: row.serviceNamesShort || row.serviceNames || '--',
      startDate: row.startDate || '--',
      endDate: row.endDate || '--',
      priority: row.priority != null ? row.priority : '--',
      action: new TableData({
        type: TableDataTypes.ACTION,
        actions: new TableActions([
          new TableAction({
            text: 'View',
            onClick: (data) => {
              const base = this.kind === 'billingPlan' ? '/accessfee/rule-requests' : '/subscription/rule-requests';
              this.openPage(`${base}/${data.id}`);
            }
          })
        ])
      })
    };
    if (this.kind === 'billingPlan') {
      return Object.assign(common, {
        zoneNames: row.zoneNames || '--',
        userSelector: row.userSelector || 'No'
      });
    }
    return Object.assign(common, {
      type: this.typeLabel(row)
    });
  }

  private openPage(url) {
    window.open(this.router.serializeUrl(this.router.createUrlTree([url])));
  }

  private statusLabel(status: string) {
    if (!status) {
      return '--';
    }
    const value = String(status).toLowerCase();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private typeLabel(row) {
    const planType = row.planType === 'REGULAR' || row.planType === 'PREPAID' ? 'PREPAID' : (row.planType || '--');
    return `${planType}\n${row.validityType || '--'}`;
  }
}
